import React, { useEffect, useState } from 'react';
import { MdAdd, MdEdit, MdDelete } from 'react-icons/md';
import DeleteModel from '../Common/DeleteModel';
import LicensureModal from './LicensureModal';
import { useAuth } from '../../Context/AuthContext';
import { saveProviderLicense, getAllProviderControlledSubstanceLicenses, getAllProviderDEALicenses, getAllProviderStateLicenses, deleteProviderLicense } from '../../Services/ProviderService';
import { toast } from 'react-toastify';


const SECTION_LABELS = [
  'State Licenses',
  'DEA Licenses',
  'State Controlled Substance Licenses',
];

const LICENSE_TYPE_IDS = {
  state: 44,
  dea: 45,
  controlled: 46,
};

const PROVIDER_ID_STORAGE_KEY = 'providerForm.providerId';
const ORGANIZATION_ID_STORAGE_KEY = 'providerForm.organizationId';

const getProviderIdFromStorage = () => {
  try {
    return localStorage.getItem(PROVIDER_ID_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to read provider ID from localStorage:', error);
    return null;
  }
};
const getOrganizationIdFromStorage = () => {
  try {
    return localStorage.getItem(ORGANIZATION_ID_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to read organization ID from localStorage:', error);
    return null;
  }
};

export default function Licensure({ initialData, providerId, onSaveSuccess, onContinue }) {
  const { token } = useAuth();
  const [stateLicenses, setStateLicenses] = useState(initialData?.stateLicenses || []);
  const [deaLicenses, setDeaLicenses] = useState(initialData?.deaLicenses || []);
  const [controlledLicenses, setControlledLicenses] = useState(initialData?.controlledSubstanceLicenses || []);
  const [activeSection, setActiveSection] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [stateLoading, setStateLoading] = useState(false);
  const [deaLoading, setDeaLoading] = useState(false);
  const [controlledLoading, setControlledLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, section: null, index: null, item: null, isLoading: false });

  const normalizeArray = (value) => (Array.isArray(value) ? value : []);

  const refreshSection = async (section) => {
    const currentProviderId = providerId || getProviderIdFromStorage();
    if (!token || !currentProviderId) return;

    try {
      if (section === 'state') setStateLoading(true);
      if (section === 'dea') setDeaLoading(true);
      if (section === 'controlled') setControlledLoading(true);
      switch (section) {
        case 'state': {
          const response = await getAllProviderStateLicenses(token, currentProviderId);
          if (response?.is_success) {
            setStateLicenses(normalizeArray(response.data));
            return normalizeArray(response.data);
          } else if (response?.is_success === false && response?.status_code === 500) {
            toast.error('Internal Server Error. Please Contact Support Team.');
          }
          return stateLicenses;
        }
        case 'dea': {
          const response = await getAllProviderDEALicenses(token, currentProviderId);
          if (response?.is_success) {
            setDeaLicenses(normalizeArray(response.data));
            return normalizeArray(response.data);
          } else if (response?.is_success === false && response?.status_code === 500) {
            toast.error('Internal Server Error. Please Contact Support Team.');
          }
          return deaLicenses;
        }
        case 'controlled': {
          const response = await getAllProviderControlledSubstanceLicenses(token, currentProviderId);
          if (response?.is_success) {
            setControlledLicenses(normalizeArray(response.data));
            return normalizeArray(response.data);
          } else if (response?.is_success === false && response?.status_code === 500) {
            toast.error('Internal Server Error. Please Contact Support Team.');
          }
          return controlledLicenses;
        }
        default:
          return [];
      }
    } catch (error) {
      console.error(`Failed to refresh ${section} licenses:`, error);
      toast.error('Unable to refresh license list.');
      return [];
    } finally {
      if (section === 'state') setStateLoading(false);
      if (section === 'dea') setDeaLoading(false);
      if (section === 'controlled') setControlledLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;

    const loadLicenses = async () => {
      // ensure UI shows loaders for all sections during initial fetch
      setStateLoading(true);
      setDeaLoading(true);
      setControlledLoading(true);
      try {
        await Promise.all([
          refreshSection('state'),
          refreshSection('dea'),
          refreshSection('controlled'),
        ]);
      } finally {
        setStateLoading(false);
        setDeaLoading(false);
        setControlledLoading(false);
      }
    };

    loadLicenses();
  }, [token, providerId]);

  const getSectionItems = (section) => {
    switch (section) {
      case 'state': return stateLicenses;
      case 'dea': return deaLicenses;
      case 'controlled': return controlledLicenses;
      default: return [];
    }
  };

  const setSectionItems = (section, items) => {
    if (section === 'state') setStateLicenses(items);
    if (section === 'dea') setDeaLicenses(items);
    if (section === 'controlled') setControlledLicenses(items);
  };

  const isSectionLoading = (section) => (section === 'state' ? stateLoading : section === 'dea' ? deaLoading : controlledLoading);

  const handleAdd = (section) => {
    setActiveSection(section);
    setEditingIndex(null);
    setShowModal(true);
  };

  const handleEdit = (section, index) => {
    setActiveSection(section);
    setEditingIndex(index);
    setShowModal(true);
  };

  const handleSave = async (values) => {
    if (!token || !activeSection) return;

    setModalLoading(true);
    try {
      const currentProviderId = providerId || getProviderIdFromStorage();
      const organizationId = getOrganizationIdFromStorage();

      const payload = {
        ...values,
        provider_id: currentProviderId,
        organization_id: organizationId,
        license_type: LICENSE_TYPE_IDS[activeSection],
        type: values.type ?? (values.id ? 'update' : 'create'),
      };

      const response = await saveProviderLicense(token, payload);
      if (response?.is_success) {
        const updatedItems = await refreshSection(activeSection);
        if (onSaveSuccess) onSaveSuccess(updatedItems);
        setShowModal(false);
        setEditingIndex(null);
        setActiveSection(null);
        toast.success(response.message || 'License saved successfully');
        return;
      } else if (response?.is_success === false && response?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
      } else {
        toast.error(response?.message || 'Failed to save license');
      }
    } catch (error) {
      console.error('Error saving provider license:', error);
      toast.error('Error saving provider license');
    } finally {
      setModalLoading(false);
    }
  };

  const openDeleteModal = (section, index) => {
    setDeleteModal({ isOpen: true, section, index, item: getSectionItems(section)[index], isLoading: false });
  };

  const closeDeleteModal = () => setDeleteModal({ isOpen: false, section: null, index: null, item: null, isLoading: false });

  const confirmDelete = async () => {
    if (deleteModal.section === null || deleteModal.index === null || deleteModal.index === undefined) return;
    setDeleteModal((prev) => ({ ...prev, isLoading: true }));
    try {
      await handleDelete(deleteModal.section, deleteModal.index);
      closeDeleteModal();
    } catch (error) {
      closeDeleteModal();
    }
  };

  const handleDelete = async (section, index) => {
    const currentItems = [...getSectionItems(section)];
    const itemToDelete = currentItems[index];
    const licenseId = itemToDelete?.id ?? itemToDelete?.license_id;
    const updated = currentItems.filter((_, i) => i !== index);

    if (!licenseId) {
      setSectionItems(section, updated);
      if (onSaveSuccess) onSaveSuccess(updated);
      return;
    }

    const originalItems = currentItems;
    setModalLoading(true);
    try {
      const response = await deleteProviderLicense(token, licenseId);
      if (response?.is_success) {
        const refreshedItems = await refreshSection(section);
        if (Array.isArray(refreshedItems)) {
          setSectionItems(section, refreshedItems);
          if (onSaveSuccess) onSaveSuccess(refreshedItems);
        } else {
          setSectionItems(section, updated);
          if (onSaveSuccess) onSaveSuccess(updated);
        }
        toast.success(response.message || 'License deleted successfully');
      } else if (response?.is_success === false && response?.status_code === 500) {
        setSectionItems(section, originalItems);
        if (onSaveSuccess) onSaveSuccess(originalItems);
        toast.error('Internal Server Error. Please Contact Support Team.');
      } else {
        setSectionItems(section, originalItems);
        if (onSaveSuccess) onSaveSuccess(originalItems);
        toast.error(response?.message || 'Failed to delete license');
      }
    } catch (error) {
      console.error('Error deleting provider license:', error);
      setSectionItems(section, originalItems);
      if (onSaveSuccess) onSaveSuccess(originalItems);
      toast.error('Error deleting provider license');
    } finally {
      setModalLoading(false);
    }
  };

  const handleContinueClick = () => {
    if (onContinue) onContinue();
  };

  const renderSection = (sectionKey, title, items) => (
    <div className="col-12">
      <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h6 className="fw-bold hc-text-primary m-0">{title}</h6>
          <button type="button" className="btn btn-sm hc-bg-primary text-white rounded-3 px-3 py-1 d-flex align-items-center gap-2" onClick={() => handleAdd(sectionKey)}>
            <MdAdd size={18} /> Add
          </button>
        </div>


        {isSectionLoading(sectionKey) ?(
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-4 text-muted small">
               No {title.toLowerCase()} added yet. Click "Add" to create one.
            </div>
          ) : (

          <div className="row row-cols-1 row-cols-md-3 row-cols-lg-5 g-3 mb-3">
            {
              items.map((item, idx) => {

              const licenseValue = item.license || item.license_number || 'Unnamed License';
              const statusValue = item.license_status_name || item.license_status || item.status_name || 'N/A';
              const issueDate = item.issue_date ? new Date(item.issue_date).toLocaleDateString() : 'N/A';
              const expiryDate = item.expiration_date ? new Date(item.expiration_date).toLocaleDateString() : 'N/A';
               
              return(
                <React.Fragment key={item.id || idx}>
              <div className="col">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                     License
                    </div>

                    <div className="fw-semibold text-dark">
                     {licenseValue}
                    </div>

                  </div>
                </div>
              </div>


  
              <div className="col">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                      Status
                    </div>

                    <div className="fw-semibold text-dark">
                      {statusValue}
                    </div>

                  </div>
                </div>
              </div>



              <div className="col">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                      Issue Date
                    </div>

                    <div className="fw-semibold text-dark">
                    {issueDate}
                    </div>

                  </div>
                </div>
              </div>



              <div className="col">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                      Expiry Date
                    </div>

                    <div className="fw-semibold text-dark">
                      {expiryDate}
                    </div>

                  </div>
                </div>
              </div>



              <div className="col">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                      Actions
                    </div>

                    <div className="d-flex gap-2">

                      <button type="button" className="btn btn-sm btn-outline-primary rounded-3 me-1" onClick={() => handleEdit(sectionKey, idx)} disabled={modalLoading}><MdEdit size={16} /></button>
                          <button type="button" className="btn btn-sm btn-danger rounded-3" onClick={() => openDeleteModal(sectionKey, idx)} disabled={modalLoading}><MdDelete size={16} /></button>

                    </div>

                  </div>
                </div>
              </div> 
                </React.Fragment>
              )
            })}
              

            </div>

            )}

      </div>
    </div>
  );

  return (
    <div className="row g-3">
      {renderSection('state', 'State Licenses', stateLicenses)}
      {renderSection('dea', 'DEA Licenses', deaLicenses)}
      {renderSection('controlled', 'State Controlled Substance Licenses', controlledLicenses)}

      <LicensureModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditingIndex(null); setActiveSection(null); }}
        onSave={handleSave}
        initialData={editingIndex !== null ? getSectionItems(activeSection)[editingIndex] : null}
        isLoading={modalLoading}
        categoryLabel={activeSection === 'state' ? 'State' : activeSection === 'dea' ? 'DEA' : 'State Controlled Substance'}
        licenseType={LICENSE_TYPE_IDS[activeSection]}
        providerId={providerId}
      />

      <DeleteModel
        isOpen={deleteModal.isOpen}
        title="Confirm Deletion"
        entityLabel="license"
        itemName={deleteModal.item ? (deleteModal.item.license || deleteModal.item.license_number || 'this license') : 'this license'}
        description="This will permanently delete this license. This action cannot be undone."
        onConfirm={confirmDelete}
        onClose={closeDeleteModal}
        isLoading={deleteModal.isLoading}
      />

      <div className="d-flex justify-content-end mt-4">
        <button
          type="button"
          className="btn btn-sm hc-bg-primary text-white rounded-3 px-4 py-2"
          onClick={handleContinueClick}
        >
          Continue
        </button>
      </div>
    </div>
  );
}
